import Joi from "joi";
import moment from "moment";
import { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getColleges,
  getPageBlocks,
  getUserTypes,
  getDepartments,
  getNotificationTypes,
  getUserPageBlocks,
  getCollegeDepartments,
} from "../../../../../action/common/commonActionCreator";
import { updateNotification } from "../../../../../action/masters/notificationActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import {
  baseURL,
  booleanOptions,
  cloneDeep,
  maxImageSize,
} from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  IEditNotification,
  INotificationData,
} from "../../../../../models/master/notification";
import { setNotification } from "../../../../../store/slices/master/notificationSlice";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const NotificationEdit = (props: IEditNotification) => {
  const dispatch = useDispatch();
  const {
    updateNotification,
    notification,
    colleges,
    getColleges,
    currentUser,
    getPageBlocks,
    getUserTypes,
    getNotificationTypes,
    getDepartments,
    departments,
    pageBlocks,
    userTypes,
    setShowEdit,
    notificationTypes,
    getAllNotifications,
    getUserPageBlocks,
    collegeDepartments,
    getCollegeDepartments,
  } = props;

  // states
  const [data, setData] = useState<INotificationData>(
    cloneDeep(notification || {})
  );
  const [initialData, setInitialData] = useState<INotificationData | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasChange, setHasChange] = useState(false);
  const [imageRemoved, setImageRemoved] = useState<boolean>(false);
  const freshmanOptionsList = Object.entries(booleanOptions)?.map(
    ([key, value]) => ({
      id: value,
      name: key,
    })
  );

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value), masterType.notification);

      if (Number(event.target.value) === UserTypes.Staff) {
        _data.collegeIds = colleges.map((college) => college.id);
        _data.departmentIds = departments.map((department) => department.id);
      } else {
        _data.collegeIds = [];
        _data.departmentIds = [];
      }
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const onFileRemove = () => {
    setData({ ...data, file: null });
    setImagePreview(null);
    setImageName(null);
    setImageRemoved(true);
    if (errors?.file) {
      const newErrors = { ...errors };
      delete newErrors.file;
      setErrors(newErrors);
    }
  };

  useEffect(() => {
    if (notification) {
      let _notification = cloneDeep(notification);
      _notification.startDate = moment(_notification.startDate).format(
        "YYYY-MM-DD"
      );
      _notification.endDate = moment(_notification.endDate).format(
        "YYYY-MM-DD"
      );
      _notification.isFreshman = _notification.isFreshman ?? null;
      setData(_notification);
      setInitialData(_notification);
      getUserPageBlocks(
        Number(_notification.userType),
        masterType.notification
      );
      if (_notification.image) {
        const imageUrl = baseURL + _notification.image;
        setImageName(_notification.image);
        setImagePreview(imageUrl);
      }
    }
  }, [notification]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllNotifications();
      setShowEdit(false);
    }
  };

  const schema = {
    title: Joi.string().required().label("Title is required"),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    startDate: Joi.string().required().label("Start Date is required"),
    endDate: Joi.string().required().label("End Date is required"),
    notificationMessage: Joi.string()
      .required()
      .label("Notification Message is required"),
    notificatonTypeId: Joi.number()
      .greater(0)
      .required()
      .label("Select Notification Type"),
    pageBlockIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Page Block"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
    isFreshman: Joi.boolean()
      .allow("", null)
      .optional()
      .label("Freshman selection is required"),
  };

  const validationData = {
    ...data,
    isFile: Boolean(data.file),
  };

  const schemaForSuperAdmin = {
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
    departmentIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Department"),
  };

  const onUpdate = async () => {
    let _schema =
      currentUser == UserTypes.SuperAdmin
        ? Joi.object().keys({ ...schema, ...schemaForSuperAdmin })
        : Joi.object().keys({ ...schema });
    const validationErrors = validateFields(validationData, _schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id != null ? data.id.toString() : "");
      formData.append(
        "userType",
        data.userType != null ? data.userType.toString() : ""
      );
      formData.append("title", data.title);
      formData.append("file", data.file);
      formData.append("isFreshman", data.isFreshman?.toString() ?? "");
      formData.append(
        "notificationMessage",
        data.notificationMessage.replace(/\s+$/g, "")
      );
      formData.append(
        "notificatonTypeId",
        data.notificatonTypeId != null ? data.notificatonTypeId.toString() : ""
      );
      formData.append("isActive", "true");
      formData.append(
        "priority",
        data.priority != null ? data.priority.toString() : ""
      );
      formData.append("startDate", data.startDate);
      formData.append("endDate", data.endDate);
      data.pageBlockIds.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.collegeIds.map((item, index) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });
      data.departmentIds.map((item, index) => {
        formData.append(`departmentIds[${index}]`, item.toString());
      });

      setLoading(true);
      setIsDisabled(true);
      try {
        await updateNotification(formData, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    getColleges();
    getUserTypes();
    getDepartments();
    getNotificationTypes();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setNotification(undefined));
    };
  }, []);

  useEffect(() => {
    if (data.collegeIds?.length > 0) {
      getCollegeDepartments(data.collegeIds.toString());
    } else {
      setData((prevData) => ({
        ...prevData,
        departmentIds: [],
      }));
    }
  }, [data.collegeIds]);

  useEffect(() => {
    if (
      collegeDepartments &&
      data.collegeIds &&
      collegeDepartments.length > 0 &&
      data.collegeIds.length > 0 &&
      (!data.departmentIds || data.departmentIds.length === 0)
    ) {
      setData((prevData) => ({
        ...prevData,
        departmentIds: collegeDepartments?.map((dept) => dept.id),
      }));
    }
  }, [collegeDepartments, data.collegeIds]);

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    errors && delete errors[name];
    const updatedData = { ...data, [name]: value };

    // Validate dates
    if (name === "endDate" && value < updatedData.startDate) {
      setErrors({
        ...errors,
        endDate: "End date cannot be earlier than the start date.",
      });
    } else if (
      name === "startDate" &&
      updatedData.endDate &&
      updatedData.endDate < value
    ) {
      setErrors({ ...errors, endDate: "Please adjust the end date." });
      updatedData.endDate = "";
    }
    setData(updatedData);
  };

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData) || imageRemoved);
    }
  }, [data, initialData]);

  useEffect(() => {
    if (pageBlocks && pageBlocks.length > 0) {
      setData((prevData) => ({
        ...prevData,
        pageBlockIds: [pageBlocks[0].id],
      }));
    }
  }, [pageBlocks]);

  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Update Notification
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowEdit(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"NotificationType "}
              onChange={onChange}
              isMandatory={true}
              value={data.notificatonTypeId}
              name={"notificatonTypeId"}
              optionsList={sortAlphabetically(notificationTypes)}
            />
            {errors?.notificatonTypeId && (
              <div className="text-danger">{errors.notificatonTypeId}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"User Type "}
              onChange={onChange}
              isMandatory={true}
              disable={currentUser == UserTypes.DepartmentAdmin ? true : false}
              value={data.userType}
              name={"userType"}
              optionsList={sortAlphabetically(
                userTypes?.filter((type) => type.id !== UserTypes.Freshman)
              )}
            />
            {errors?.userType && (
              <div className="text-danger">{errors.userType}</div>
            )}
          </div>
          {data.userType === UserTypes.Student && (
            <div className="col-lg-12 form-group">
              <FloatingLabelDropDown
                label={"Is Freshman"}
                onChange={onChange}
                isMandatory={true}
                value={data.isFreshman}
                name={"isFreshman"}
                optionsList={freshmanOptionsList}
              />
              {errors?.freshman && (
                <div className="text-danger">{errors.freshman}</div>
              )}
            </div>
          )}
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Title "}
              onChange={onChange}
              isMandatory={true}
              value={data.title}
              name={"title"}
              maxLength={50}
            />
            {errors?.title && <div className="text-danger">{errors.title}</div>}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">Image</label>
            <div className="inputfile-flex">
              {imagePreview && (
                <div className="inputfile-img">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
              )}
              <div className="inputfile-wrap">
                <input
                  type="file"
                  name="file"
                  id="image"
                  className="form-control d-none"
                  required
                  onChange={onFileChange}
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon"
                />
                <div className="inputfile">
                  <label htmlFor="image">Choose File</label>
                  <span>
                    {imageName
                      ? imageName.trim().split("/").pop()
                      : "No File Choosen"}
                  </span>
                  {imagePreview && (
                    <button
                      type="button"
                      className="btn-close"
                      onClick={onFileRemove}
                      title="Remove image"
                    ></button>
                  )}
                </div>
              </div>
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-12 form-group">
            <label className="profile_details_text">Notification Message</label>
            <textarea
              name="notificationMessage"
              value={data.notificationMessage}
              onChange={onChange}
              maxLength={1000}
            ></textarea>
            {errors?.notificationMessage && (
              <div className="text-danger">{errors.notificationMessage}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">Start Date</label>
            <input
              type="date"
              name="startDate"
              className="form-control"
              onChange={onChangeDate}
              value={data.startDate}
              onKeyDown={(e) => e.preventDefault()}
              inputMode="none"
              min={new Date().toISOString().split("T")[0]}
            />
            {errors?.startDate && (
              <div className="text-danger">{errors.startDate}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">End Date</label>
            <input
              type="date"
              name="endDate"
              className="form-control"
              onChange={onChangeDate}
              value={data.endDate}
              onKeyDown={(e) => e.preventDefault()}
              inputMode="none"
              min={
                data.startDate
                  ? data.startDate
                  : new Date().toISOString().split("T")[0]
              }
            />
            {errors?.endDate && (
              <div className="text-danger">{errors.endDate}</div>
            )}
          </div>
          {currentUser === UserTypes.SuperAdmin && (
            <div className="col-md-6 form-group">
              <MultipleSelect
                label={"Departments "}
                onChange={onChange}
                isMandatory={true}
                values={data.departmentIds as number[]}
                name={"departmentIds"}
                optionList={sortAlphabetically(collegeDepartments)}
                disabled={data.collegeIds?.length === 0}
              />
              {errors?.departmentIds && (
                <div className="text-danger">{errors.departmentIds}</div>
              )}
            </div>
          )}
          <div
            className={`col-md-${
              currentUser === UserTypes.DepartmentAdmin ? 12 : 6
            } form-group`}
          >
            <FloatingLabelTextInput
              label={"Page-Block "}
              onChange={onChange}
              isMandatory={false}
              value={pageBlocks?.[0]?.name}
              name={"pageBlockIds"}
              disable
            />
            {errors?.pageBlockIds && (
              <div className="text-danger">{errors.pageBlockIds}</div>
            )}
          </div>

          {currentUser === UserTypes.SuperAdmin && (
            <div className="col-md-12 form-group">
              <MultipleSelect
                label={"University Management"}
                onChange={onChange}
                isMandatory={true}
                values={data.collegeIds as number[]}
                name={"collegeIds"}
                optionList={sortAlphabetically(colleges)}
              />
              {errors?.collegeIds && (
                <div className="text-danger">{errors.collegeIds}</div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onUpdate}
            disabled={isDisabled || !hasChange}
          >
            Update
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowEdit(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  updateNotification: (data: any, id: number, callBack: Function) =>
    updateNotification(data, id, callBack),
  getColleges: () => getColleges(),
  getPageBlocks: () => getPageBlocks(),
  getUserTypes: () => getUserTypes(),
  getDepartments: () => getDepartments(),
  getNotificationTypes: () => getNotificationTypes(),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
  getCollegeDepartments: (collegeIds: string) =>
    getCollegeDepartments(collegeIds),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
    notificationTypes: state.common.notificationTypes,
    departments: state.common.departments,
    collegeDepartments: state.common.collegeDepartments,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NotificationEdit);
